import { useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useOfflineStorage } from '@/hooks/useOfflineStorage';
import { useMobileRouter } from '@/hooks/useMobileRouter';
import { useNativeFeatures } from '@/hooks/useNativeFeatures';
import { LazyComponentWrapper } from '@/components/mobile/LazyMobileComponent';
import {
  Camera,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Zap,
  Smartphone,
  Wifi,
  WifiOff
} from 'lucide-react';

interface ProcessingJob {
  id: string;
  name: string;
  type: 'camera' | 'upload' | 'scan';
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  createdAt: Date;
  estimatedCompletion?: Date;
}

export const MobileBillProcessing = () => {
  const [processingJobs, setProcessingJobs] = useState<ProcessingJob[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const { isOnline, addOfflineAction } = useOfflineStorage();
  const { navigateWithTransition } = useMobileRouter();
  const { takePicture, isNative } = useNativeFeatures();

  const handleCameraCapture = useCallback(async () => {
    if (!isNative) {
      toast({
        title: "Camera not available",
        description: "Camera features are only available in the mobile app",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    try {
      const imageData = await takePicture();
      
      const newJob: ProcessingJob = {
        id: `camera_${Date.now()}`,
        name: `Camera capture ${new Date().toLocaleTimeString()}`,
        type: 'camera',
        status: 'pending',
        progress: 0,
        createdAt: new Date(),
        estimatedCompletion: new Date(Date.now() + 30000) // 30 seconds
      };

      setProcessingJobs(prev => [newJob, ...prev]);

      if (!isOnline) {
        await addOfflineAction({
          type: 'create',
          endpoint: '/api/bills/process',
          data: { imageData, source: 'camera' }
        });
        
        toast({
          title: "Saved for later",
          description: "Bill will be processed when you're back online"
        });
      } else {
        // Simulate processing
        simulateProcessing(newJob.id);
      }
    } catch (error) {
      toast({
        title: "Capture failed",
        description: "Could not capture image. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  }, [isNative, takePicture, toast, isOnline, addOfflineAction]);

  const simulateProcessing = useCallback((jobId: string) => {
    const updateProgress = (progress: number) => {
      setProcessingJobs(prev => prev.map(job => 
        job.id === jobId 
          ? { ...job, status: 'processing' as const, progress }
          : job
      ));
    };

    // Simulate progressive processing
    setTimeout(() => updateProgress(25), 500);
    setTimeout(() => updateProgress(50), 1500);
    setTimeout(() => updateProgress(75), 2500);
    setTimeout(() => {
      setProcessingJobs(prev => prev.map(job => 
        job.id === jobId 
          ? { ...job, status: 'completed' as const, progress: 100 }
          : job
      ));
      
      toast({
        title: "Processing complete",
        description: "Your bill has been successfully processed"
      });
    }, 3500);
  }, [toast]);

  const handleFileUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.multiple = true;
    
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        Array.from(files).forEach((file, index) => {
          const newJob: ProcessingJob = {
            id: `upload_${Date.now()}_${index}`,
            name: file.name,
            type: 'upload',
            status: 'pending',
            progress: 0,
            createdAt: new Date(),
            estimatedCompletion: new Date(Date.now() + 45000)
          };

          setProcessingJobs(prev => [newJob, ...prev]);
          
          if (!isOnline) {
            addOfflineAction({
              type: 'create',
              endpoint: '/api/bills/process',
              data: { file: file.name, source: 'upload' }
            });
          } else {
            simulateProcessing(newJob.id);
          }
        });
      }
    };
    
    input.click();
  }, [isOnline, addOfflineAction, simulateProcessing]);

  const getStatusIcon = (status: ProcessingJob['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-destructive" />;
      case 'processing':
        return <Zap className="h-5 w-5 text-primary animate-pulse" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: ProcessingJob['status']) => {
    switch (status) {
      case 'completed': return 'bg-success/10 text-success';
      case 'error': return 'bg-destructive/10 text-destructive';
      case 'processing': return 'bg-primary/10 text-primary';
      default: return 'bg-muted/50 text-muted-foreground';
    }
  };

  return (
    <LazyComponentWrapper>
      <div className="space-y-6">
        {/* Connection Status */}
        <Card className={`border-l-4 ${isOnline ? 'border-l-success' : 'border-l-warning'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {isOnline ? (
                <Wifi className="h-5 w-5 text-success" />
              ) : (
                <WifiOff className="h-5 w-5 text-warning" />
              )}
              <div>
                <p className="font-medium">
                  {isOnline ? 'Connected' : 'Offline Mode'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {isOnline 
                    ? 'Bills will be processed immediately' 
                    : 'Bills will be processed when connection is restored'
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Mobile Bill Capture
            </CardTitle>
            <CardDescription>
              Capture and process bills instantly with your mobile device
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={handleCameraCapture}
              disabled={isProcessing || !isNative}
              className="w-full h-12"
              size="lg"
            >
              <Camera className="h-5 w-5 mr-2" />
              {isProcessing ? 'Capturing...' : 'Take Photo'}
            </Button>
            
            <Button
              onClick={handleFileUpload}
              variant="outline"
              disabled={isProcessing}
              className="w-full h-12"
              size="lg"
            >
              <Upload className="h-5 w-5 mr-2" />
              Upload Files
            </Button>

            {!isNative && (
              <p className="text-xs text-muted-foreground text-center">
                Install the mobile app for camera features
              </p>
            )}
          </CardContent>
        </Card>

        {/* Processing Queue */}
        {processingJobs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Processing Queue
                <Badge variant="secondary" className="ml-auto">
                  {processingJobs.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {processingJobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center gap-3 p-3 border rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {getStatusIcon(job.status)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium truncate">
                        {job.name}
                      </p>
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${getStatusColor(job.status)}`}
                      >
                        {job.status}
                      </Badge>
                    </div>
                    
                    {job.status === 'processing' && (
                      <Progress value={job.progress} className="h-2 mb-1" />
                    )}
                    
                    <p className="text-xs text-muted-foreground">
                      {job.type === 'camera' ? 'Camera capture' : 'File upload'} • 
                      {job.createdAt.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Quick Navigation */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            onClick={() => navigateWithTransition('/dashboard/bills')}
            className="h-16 flex-col gap-2"
          >
            <FileText className="h-6 w-6" />
            <span className="text-sm">View Bills</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={() => navigateWithTransition('/dashboard/reports')}
            className="h-16 flex-col gap-2"
          >
            <CheckCircle className="h-6 w-6" />
            <span className="text-sm">Reports</span>
          </Button>
        </div>
      </div>
    </LazyComponentWrapper>
  );
};